import React, { useEffect, useState } from 'react';
import ChatSendIcon from 'components/atoms/icons/chatSendIcon';
import BaseButton from 'components/atoms/baseButton';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import EmptyNoteIcon from 'components/atoms/icons/emptyNoteIcon';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import { useUnregisteredConversationNotes } from 'infrastructure/hooks/smsDashboard/useUnregisteredConversationNotes';
import { useCreateUnregisteredConversationNotes } from 'infrastructure/hooks/smsDashboard/useCreateUnregisteredConversationNotes';
import ThreeDotsIcon from 'components/atoms/icons/threeDotsIcon';
import COLORS from 'assets/styles/colors';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useDeleteUnregisteredConversationNotes } from 'infrastructure/hooks/smsDashboard/useDeleteUnregisteredConversationNotes';
import BaseTextArea from 'components/atoms/base-textarea';

import s from './styles.module.scss';
import EditNoteModal from './editNote';

import type { RootState } from 'store';

type Note = {
  guid: string;
  note: string;
  patientGuid: string;
  createdAt: string;
  createdBy: string;
  author: string;
};

interface InternalUnregisteredConversationNotesProps {
  setNotesCount: React.Dispatch<React.SetStateAction<number>>;
}

const InternalUnregisteredConversationNotes: React.FC<
  InternalUnregisteredConversationNotesProps
> = ({ setNotesCount }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState<string>('');
  const [menuOpenStates, setMenuOpenStates] = useState<{
    [noteId: string]: boolean;
  }>({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentNote, setCurrentNote] = useState({} as Note);

  const { Alert, showAlert } = useAlert();
  const { currentConversation } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const {
    unregisteredConversationNotes,
    unregisteredConversationNotesLoading,
  } = useUnregisteredConversationNotes({
    conversationId: currentConversation?.id,
  });

  const {
    createUnregisteredConversationNote,
    createUnregisteredConversationNoteLoading,
  } = useCreateUnregisteredConversationNotes({
    onSuccess() {
      setValue('');
    },
  });

  const {
    deleteUnregisteredConversationNote,
    deleteUnregisteredConversationNoteLoading,
  } = useDeleteUnregisteredConversationNotes({
    onSuccess: () => {
      notificationController.success({
        message: t('messages.deleteSuccess', { value: t('labels.note') }),
      });
    },
  });

  const onSubmit = async () => {
    createUnregisteredConversationNote({
      conversationId: currentConversation?.id,
      note: value,
    });
  };

  const formatDate = (date: string) =>
    DateFormatter({
      date,
      format: DateFormats['MM-dd-yyyy | hh:mm a'],
    });
  const handleMenuClick = (noteId: string) => {
    setMenuOpenStates((prevStates) => {
      const newStates = { ...prevStates };
      if (!newStates[noteId]) {
        Object.keys(newStates).forEach((id) => {
          newStates[id] = false;
        });
        newStates[noteId] = true;
      } else {
        newStates[noteId] = false;
      }
      return newStates;
    });
  };
  const handleDelete = async (guid: string) => {
    const res = await showAlert({
      title: t('confirm.deleteValue', { value: t('labels.note') }),
      message: t('confirm.deleteValueMessage', { value: t('labels.note') }),
      messageTitle: t('confirm.areYouSureYouWantDeleteThis', {
        value: t('labels.note'),
      }),
      type: 'danger',
    });
    if (res) {
      deleteUnregisteredConversationNote({
        conversationId: currentConversation?.id,
        noteGuid: guid,
      });
    }
  };

  useEffect(() => {
    if (
      !unregisteredConversationNotesLoading &&
      unregisteredConversationNotes
    ) {
      setNotesCount(unregisteredConversationNotes.length);
    }
    if (unregisteredConversationNotesLoading) {
      setNotesCount(0);
    }
  }, [unregisteredConversationNotes, unregisteredConversationNotesLoading]);

  return (
    <>
      <div className={s['chat-profile']}>
        <div className={s['notes-wrapper']}>
          <div className={s.notes}>
            {!unregisteredConversationNotesLoading ? (
              <>
                {unregisteredConversationNotes.length > 0 &&
                  unregisteredConversationNotes.map((note: Note) => (
                    <div className={s.note} key={note.guid}>
                      <div className={s['note-header']}>
                        <div className={s['note-date']}>
                          <span>{formatDate(note.createdAt)}</span>
                        </div>

                        <div
                          className={s['note-menu']}
                          onClick={() => handleMenuClick(note.guid)}
                        >
                          <ThreeDotsIcon
                            width="13.71px"
                            height="13.71px"
                            color={COLORS.BUOYANT}
                          />

                          {menuOpenStates[note.guid] && (
                            <div className={s['note-menu-list']}>
                              <div
                                className={s['note-menu-item']}
                                onClick={() => {
                                  setCurrentNote(note);
                                  setIsEditModalOpen(true);
                                }}
                              >
                                <span>{t('controls.edit')}</span>
                              </div>
                              <div
                                className={s['note-menu-item']}
                                onClick={() => handleDelete(note.guid)}
                              >
                                <span>{t('controls.delete')}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={s['note-body']}>{note.note}</div>
                      <div className={s['note-author']}>
                        <span>{note.author}</span>
                      </div>
                    </div>
                  ))}
                {unregisteredConversationNotes.length === 0 && (
                  <div className={s.empty}>
                    <EmptyNoteIcon />
                    <p>{t('smsDashboard.empty_notes')}</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className={s.note}>
                  <div className={s['note-body']}>
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                  </div>
                  <div className={s['note-date']}>
                    <Skeleton width={140} height={18} />
                  </div>
                </div>
                <div className={s.note}>
                  <div className={s['note-body']}>
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                  </div>
                  <div className={s['note-date']}>
                    <Skeleton width={140} height={18} />
                  </div>
                </div>
                <div className={s.note}>
                  <div className={s['note-body']}>
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                    <Skeleton width={270} height={20} />
                  </div>
                  <div className={s['note-date']}>
                    <Skeleton width={140} height={18} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className={`${s['messages-input-wrapper']} ${s['note-input']}`}>
        <div className={s['chat-form-wrapper']}>
          <div className={s['input-wrapper']}>
            <BaseTextArea
              value={value}
              placeholder={t('smsDashboard.type_note')}
              onChange={(message: string) => setValue(message)}
              disabled={
                unregisteredConversationNotesLoading ||
                createUnregisteredConversationNoteLoading ||
                deleteUnregisteredConversationNoteLoading
              }
              rows={1.03}
              maxRows={6.9}
              plaintext={false}
              style={{
                padding: '8.5px 57px 8.5px 12px',
                lineHeight: '15.6px',
                maxHeight: '200px',
              }}
              onKeyDown={(e) => {
                if (
                  (e.key === 'Enter' || e.key === 'Return') &&
                  (e.ctrlKey || e.metaKey)
                ) {
                  e.preventDefault();
                  if (
                    !value.trim() ||
                    unregisteredConversationNotesLoading ||
                    createUnregisteredConversationNoteLoading ||
                    deleteUnregisteredConversationNoteLoading
                  ) {
                    return;
                  }
                  onSubmit();
                }
              }}
              autoFocus
            />
            <div className={s['send-button']}>
              <BaseButton
                type="secondary"
                disabled={value === ''}
                loading={
                  unregisteredConversationNotesLoading ||
                  createUnregisteredConversationNoteLoading ||
                  deleteUnregisteredConversationNoteLoading
                }
                onClick={onSubmit}
                icon={<ChatSendIcon />}
              />
            </div>
          </div>
        </div>
      </div>
      {Alert}
      {isEditModalOpen && (
        <EditNoteModal
          isOpen={isEditModalOpen}
          toggle={() => {
            setIsEditModalOpen((prev) => !prev);
            setCurrentNote({} as Note);
          }}
          note={currentNote.note}
          conversationId={currentConversation.id}
          noteGuid={currentNote.guid}
        />
      )}
    </>
  );
};

export default InternalUnregisteredConversationNotes;
